'use client'

import { useEffect, useState } from 'react'

import { Button, Cell, Dialog, Navigation } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  ConversationEducationModel,
  EducationAction,
  EducationType,
  fetchConversationEducation,
  markEducationAsRead,
  transformNewEducationDto,
} from '@marketplace-web/conversation-education/conversation-education-data'
import {
  useAbTest,
  useTrackAbTest,
  useTrackAbTestCallback,
} from '@marketplace-web/feature-flags/ab-tests-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useFetch } from '@marketplace-web/core-api/api-client-util'

import ConversationEducationContent from '../ConversationEducationContent'
import { GO_TO_WALLET_URL } from '../../constants/routes'

type Props = {
  conversation?: ConversationEducationModel
}

const SUPPORTED_TYPES = [
  EducationType.InConversationSold,
  EducationType.InConversationTransactionCompleted,
  EducationType.InConversationSoldHAOV,
  EducationType.InConversationTransactionCompletedHAOV,
  EducationType.InConversationSoldElectronics,
  EducationType.InConversationSoldElectronicsEvs,
  EducationType.InConversationTransactionCompletedElectronics,
  EducationType.InConversationTransactionCompletedElectronicsEvs,
]

const ConversationEducationModal = ({ conversation }: Props) => {
  const translate = useTranslate('conversation.education.modal')
  const firstTimeSellerElectronicsAbTest = useAbTest(
    'first_time_electronics_and_evs_seller_modals_improvement',
  )
  const trackExpose = useTrackAbTestCallback()

  const [showEducation, setShowEducation] = useState(false)

  const newEducationEndpointAbTest = useAbTest('new_messaging_education_endpoint')
  useTrackAbTest(newEducationEndpointAbTest)
  const isNewEducationEndpointEnabled = useFeatureSwitch('web_new_messaging_education_endpoint')

  const isNewConversationEducationVisible = useFeatureSwitch('web_new_conversation_education')

  const isNewEducationEnabled =
    isNewEducationEndpointEnabled && newEducationEndpointAbTest?.variant === 'on'

  const { fetch: getEducation, transformedData: educationData } = useFetch(
    fetchConversationEducation,
    response => transformNewEducationDto(response.education),
  )

  useEffect(() => {
    if (!isNewEducationEndpointEnabled) return
    if (newEducationEndpointAbTest?.variant !== 'on') return

    getEducation(conversation?.transactionId, conversation?.transaction?.status || undefined)
  }, [
    conversation?.transaction?.status,
    conversation?.transactionId,
    getEducation,
    isNewEducationEndpointEnabled,
    newEducationEndpointAbTest?.variant,
  ])

  useEffect(() => {
    if (newEducationEndpointAbTest?.variant === 'on' && isNewEducationEndpointEnabled) return

    setShowEducation(!!conversation?.education)
  }, [conversation, isNewEducationEndpointEnabled, newEducationEndpointAbTest?.variant])

  useEffect(() => {
    if (!isNewEducationEndpointEnabled) return
    if (newEducationEndpointAbTest?.variant !== 'on') return

    setShowEducation(!!educationData)
  }, [educationData, isNewEducationEndpointEnabled, newEducationEndpointAbTest?.variant])

  useEffect(() => {
    if (showEducation && firstTimeSellerElectronicsAbTest)
      trackExpose(firstTimeSellerElectronicsAbTest)
  }, [showEducation, firstTimeSellerElectronicsAbTest, trackExpose])

  if (!conversation?.education && !isNewEducationEnabled) return null

  if (isNewEducationEnabled && !educationData) return null

  const closeModal = async () => {
    if (!isNewEducationEnabled && conversation?.education?.type) {
      await markEducationAsRead(conversation.education.type)
    }

    if (isNewEducationEnabled && educationData) {
      await markEducationAsRead(educationData.type)
    }

    setShowEducation(false)
  }

  const handleGoToWalletActionClick = async () => {
    if (!isNewEducationEnabled && conversation?.education?.type) {
      await markEducationAsRead(conversation.education.type)
    }

    if (isNewEducationEnabled && educationData) {
      await markEducationAsRead(educationData.type)
    }

    navigateToPage(GO_TO_WALLET_URL)
  }

  const renderNavigation = () => (
    <div className="conversation-education__background">
      <Navigation
        theme="transparent"
        right={
          <Button
            aria={{
              'aria-label': translate('a11y.actions.close'),
            }}
            styling="flat"
            theme="amplified"
            iconName={X24}
            inline
            onClick={closeModal}
            testId="conversation-education-modal-close-button"
          />
        }
      />
    </div>
  )

  const renderContent = () => {
    let type: EducationType | undefined

    if (isNewEducationEnabled) {
      type = educationData?.type
    } else {
      type = conversation?.education?.type
    }

    if (!type) return null

    return (
      <ScrollableArea>
        <ConversationEducationContent conversation={conversation} type={type} />
      </ScrollableArea>
    )
  }

  const renderActionButton = () => {
    let action: EducationAction | undefined

    if (isNewEducationEnabled) {
      action = educationData?.action
    } else {
      action = conversation?.education?.action
    }

    if (action === EducationAction.Read) {
      return (
        <Cell theme="transparent">
          <Button
            styling="filled"
            onClick={closeModal}
            text={translate('actions.go_to_conversation')}
            testId="conversation-education-modal-go-to-conversation-button"
          />
        </Cell>
      )
    }

    if (action === EducationAction.GoToWallet) {
      return (
        <Cell theme="transparent">
          <Button
            styling="filled"
            onClick={handleGoToWalletActionClick}
            text={translate('actions.got_to_wallet')}
            testId="conversation-education-modal-go-to-wallet-button"
          />
        </Cell>
      )
    }

    return null
  }

  let type: EducationType | undefined

  if (isNewEducationEnabled) {
    type = educationData?.type
  } else {
    type = conversation?.education?.type
  }

  if (!type) return null
  if (!SUPPORTED_TYPES.includes(type)) return null
  if (!isNewConversationEducationVisible) return null

  return (
    <Dialog show={showEducation} hasScrollableContent>
      <div className="u-fill-width">
        {renderNavigation()}
        {renderContent()}
        {renderActionButton()}
      </div>
    </Dialog>
  )
}

export default ConversationEducationModal
